import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { useTab } from '@/common/entityFramework/entityDetail/TabContext';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { Form } from '@/common/form/Form';
import { FormFieldGroupContainer } from '@/common/form/FormFieldGroupContainer';
import { DynamicFormField } from '@/common/form/formFields/DynamicFormField';
import { FormSection } from '@/common/form/FormSection';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { EntityUtils } from '@/common/utils/EntityUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { faSave, faTimes } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const IndirSaleLineForm = () => {

    const formRef = useRef();

    const rsrc = useRsrc()

    const { tabLabel } = useTab();

    const { navState, assign } = useNavState();

    const { asOfDate, setExportReq, entityDef, entityFormMode, setEntityFormMode } = useEntityMgr();

    const { getFieldRoleSet } = usePrincipal();

    const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();

    const [entityId, setEntityId] = useState(navState.entityId);
    const [fieldDefs, setFieldDefs] = useState();


    useEffect(() => {
        const resFieldDefs = [
            { field: 'salesAmtMatch', displayName: 'Sales Amount Match', precedence: 64, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'chbkAmtMatch', displayName: 'Chargeback Amount Match', precedence: 67, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'ctrtAmtMatch', displayName: 'Contract Amount Match', precedence: 70, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'progMatch', displayName: 'Program Match', precedence: 76, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'shipCustPrimIdentMatch', displayName: 'Primary Ship to Customer Identifier Match', precedence: 81, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'shipCustSecIdent', displayName: 'Secondary Ship to Customer Identifier', precedence: 83, section: 'Resolution', editLevel: 'NEVER' },
            { field: 'shipCustSecIdentMatch', displayName: 'Secondary Ship to Customer Identifier Match', precedence: 84, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'intShipCustName', displayName: 'Ship to Customer Name', precedence: 87, section: 'Resolution', editLevel: 'NEVER' },
            { field: 'shipCustNameMatch', displayName: 'Ship to Customer Name Match', precedence: 88, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'intShipCustAddr', displayName: 'Ship to Customer Address', precedence: 90, section: 'Resolution', editLevel: 'NEVER' },
            { field: 'shipCustAddrMatch', displayName: 'Ship to Customer Address Match', precedence: 91, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'intShipCustCity', displayName: 'Ship to Customer City', precedence: 93, section: 'Resolution', editLevel: 'NEVER' },
            { field: 'shipCustCityMatch', displayName: 'Ship to Customer City Match', precedence: 94, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'intShipCustState', displayName: 'Ship to Customer State', precedence: 96, section: 'Resolution', editLevel: 'NEVER' },
            { field: 'shipCustStateMatch', displayName: 'Ship to Customer State Match', precedence: 97, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'intShipCustZip', displayName: 'Ship to Customer Zip', precedence: 99, section: 'Resolution', editLevel: 'NEVER' },
            { field: 'shipCustZipMatch', displayName: 'Ship to Customer Zip Match', precedence: 100, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' },
            { field: 'prodMatch', displayName: 'Product Match', precedence: 103, section: 'Resolution', type: 'BOOLEAN', editLevel: 'NEVER' }
        ];
        rsrc.fieldDefs('sc', 'INDIR_SALE_LINE').then(indirSaleLineFieldDefs => {
            indirSaleLineFieldDefs.push(...resFieldDefs);
            indirSaleLineFieldDefs.sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
            indirSaleLineFieldDefs = indirSaleLineFieldDefs.filter(fieldDef => !fieldDef.checkboxSelection)
            indirSaleLineFieldDefs = EntityUtils.filterAuthorizedFields('sc', 'INDIR_SALE_LINE', indirSaleLineFieldDefs, getFieldRoleSet);

            indirSaleLineFieldDefs.sort((a, b) => (a.precedence || 0) - (b.precedence || 0))

            setDisabled(indirSaleLineFieldDefs);
            setFieldDefs(indirSaleLineFieldDefs);
        });
    }, []);

    useEffect(() => {
        subscrToEntityChange('sc', 'INDIR_SALE_LINE', [entityId]);
        return () => {
            unsubscrToEntityChange('sc', 'INDIR_SALE_LINE', [entityId]);
        };
    }, [entityId]);

    // Responsible for setting the disabled status of fieldDefs
    const setDisabled = (fieldDefs) => {
        if (!fieldDefs) return;
        fieldDefs.forEach(fieldDef => {
            if (entityFormMode === EntityFormMode.VIEW) {
                fieldDef.disabled = true;
            } else if (entityFormMode === EntityFormMode.ADD) {
                fieldDef.disabled = fieldDef.editLevel === 'NEVER';
            } else {
                fieldDef.disabled = fieldDef.editLevel === 'NEVER' || fieldDef.editLevel === 'ADD_ONLY' || (fieldDef.editLevel === 'SOR' && !formRef.current.formValues?.sor);
            }
        });
    }

    // Subscribe to the mode
    useEffect(() => {
        if (!fieldDefs) return;
        setDisabled(fieldDefs);
        setFieldDefs([...fieldDefs]); // Putting the fieldDefs in a new array reference forces an update
    }, [entityFormMode])

    const refreshForm = () => {

        const currFormRef = formRef.current;
        if (currFormRef == null) return;

        // If adding, we use anything supplied in the navState entity onlys
        // if (entityFormMode === EntityFormMode.ADD) {
        //     currFormRef.setFormValues(navState.entityObj);
        //     return;
        // }

        const rowsReq = {}
        const exprs = [ExprUtils.whereParams('id', entityId)];

        // As Of Date
        if (asOfDate) {
            exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', asOfDate))
            exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER_OR_EQUAL', asOfDate))
            rowsReq.hist = true;
        }

        rowsReq.expr = ExprUtils.and(...exprs);

        if (navState.partition) {
            // rowsReq.partition = navState.partition;
        }

        buildAndSetExportReq(rowsReq);

        const reqParam = { entityId };

        rsrc.data('sc', 'INDIR_SALE_LINE_INFO_DATA_PLUGIN', reqParam).then(data => {
            currFormRef.setFormValues(data);
        });
    }

    const buildAndSetExportReq = (rowsReq) => {
        const exportReq = {
            timeZoneName: DateUtils.timeZoneName(),
            name: entityDef.displayName + ' Export',
            module: 'sc',
            entity: 'INDIR_SALE_LINE',
            entityId,
            label: tabLabel,
            rowsReq,
            fieldDefs,
            uiType: 'DETAIL'
        }

        setExportReq(exportReq);
    }

    const save = () => {
        if (formRef.current.hasErrorState()) {
            formRef.current.touchAll();
            return;
        }

        const newEntityObj = formRef.current.formValues;

        // TODO: discuss SOR

        rsrc.save('sc', 'INDIR_SALE_LINE', newEntityObj).then(entityObj => {
            assign('entityId', entityObj.id);
            setEntityId(entityObj.id);
            setEntityFormMode(EntityFormMode.VIEW);
        })
    }

    const cancel = () => {
        if (entityFormMode === EntityFormMode.ADD) {
            window.history.back();
            return;
        }

        setEntityFormMode(EntityFormMode.VIEW);
        refreshForm();
    }

    useEffect(() => {
        if (fieldDefs == null) return;
        refreshForm();
    }, [refreshTrigger, asOfDate, fieldDefs])

    if (fieldDefs == null) {
        return;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0 }}>
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, pb: 0 }} className={entityFormMode === EntityFormMode.VIEW ? 'entity-form-wrapper' : null}>
                <Form
                    ref={formRef}
                    children={Children({ fieldDefs })}
                />
            </Box>
            {entityFormMode !== EntityFormMode.VIEW && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        columnGap: '10px',
                        pr: 2,
                    }}
                >
                    <Button sx={{ minWidth: '100px' }} onClick={cancel}>
                        <Box component="span" sx={{ mr: 1.3 }}>
                            <FontAwesomeIcon icon={faTimes} />
                        </Box>
                        Cancel
                    </Button>
                    <Button sx={{ minWidth: '100px' }} data-test-id="save-button" onClick={save}>
                        <Box component="span" sx={{ mr: 1.3 }}>
                            <FontAwesomeIcon icon={faSave} />
                        </Box>
                        Save
                    </Button>
                </Box>
            )}
        </Box >
    );
}

const Children = ({ fieldDefs }) => {
    const sections = [];
    const sectionToFieldDefs = {};
    fieldDefs.forEach(fieldDef => {
        const section = fieldDef.section;
        if (!sections.includes(section)) {
            sections.push(section);
        }

        let sectionFieldDefs = sectionToFieldDefs[section];
        if (!sectionFieldDefs) {
            sectionFieldDefs = [];
            sectionToFieldDefs[section] = sectionFieldDefs;
        }
        sectionFieldDefs.push(fieldDef);
    })

    // Audit always comes last
    sections.sort((sectionA, sectionB) =>
        (sectionA === 'Audit') - (sectionB === 'Audit')
    );

    return (sections.map(section =>
        section === 'Resolution' ?
            <FormSection key={section} title={section}>
                <ResolutionContainer>
                    {fieldDefsToDynamicFormFields(sectionToFieldDefs[section])}
                </ResolutionContainer>
            </FormSection>
            :
            <FormSection key={section} title={section}>
                <FormFieldGroupContainer>
                    {fieldDefsToDynamicFormFields(sectionToFieldDefs[section])}
                </FormFieldGroupContainer>
            </FormSection>
    ))
}

const fieldDefsToDynamicFormFields = (fieldDefs) => {
    return fieldDefs.map(fieldDef => {
        if (fieldDef.hide) return null;
        return fieldDef.component
            ? fieldDef.component
            : <DynamicFormField key={fieldDef.field} fieldDef={fieldDef} />;
    });
}

const ResolutionContainer =
    styled(Box)({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        columnGap: '16px',
        rowGap: '8px',
        margin: '10px 0',
    });